import { isDate, isNumber, isString } from "lodash";
import { DateTime } from "luxon";
import type { DateTimeUnit, DurationLikeObject } from "luxon";

import { type DateLike, type DateFormat, isDateLike } from "./date-builder.types";

type DateBuilderOptions = { format?: string };

/*****
 * dateBuilder
 *
 * @deprecated write a util for advanced date manipulation or use built in luxon methods
 */
export const dateBuilder = (dateInput: DateLike, options?: DateBuilderOptions) =>
  new DateBuilder(dateInput, options);

/**********
 * BaseDateBuilder
 *    Perform date comparison and data type conversion
 */
class BaseDateBuilder {
  protected readonly dt: DateTime;

  constructor(dateInput: DateLike, options?: DateBuilderOptions) {
    if (!isDateLike(dateInput)) {
      throw new Error("Invalid date");
    }

    const dt = translateDateLikeToDateTime(dateInput, options);
    if (!dt.isValid) {
      throw new Error("Invalid date");
    }

    this.dt = dt;
  }

  /**********
   * isOnSameDay
   *    Determines whether the given date is on the same day as the initial date
   *
   * @param dateInput
   * @param options
   */
  isOnSameDay(dateInput: DateLike, options?: DateBuilderOptions): boolean {
    const compareDt = new BaseDateBuilder(dateInput, options);
    return this.dt.toISODate() === compareDt.ISODate;
  }

  /**********
   * numMonthsApart
   *    Calculates the number of months between two dates
   *
   * @param dateInput
   * @param options
   */
  numMonthsApart(dateInput: DateLike, options?: DateBuilderOptions): number {
    const compareDt = new DateBuilder(dateInput, options);
    const diff = this.dt.startOf("month").diff(compareDt.startOf("month").DateTime, "months");

    return Math.abs(diff.months);
  }

  /**********
   * numDaysApart
   *    Calculates the number of days between two dates
   *
   * @param dateInput
   * @param options
   */
  numDaysApart(dateInput: DateLike, options?: DateBuilderOptions): number {
    const compareDt = new DateBuilder(dateInput, options);
    const diff = this.dt.startOf("day").diff(compareDt.startOf("day").DateTime, "days");

    return Math.abs(diff.days);
  }

  /**********
   * toFormat
   *    Translates date into formatted string
   *
   * @param format
   */
  toFormat(format: DateFormat): string {
    return this.dt.toFormat(format);
  }

  /**********
   * ISO
   *    Translates date into ISO-8601 string
   *
   *    Ex. toDateFormatter(new Date()).ISO => "2023-11-11T12:00:00.000Z
   */
  get ISO(): string {
    return this.dt.toISO() ?? "";
  }

  /**********
   * ISODate
   *    Translates date into ISO date string
   *    Ex. toDateFormatter(new Date()).ISO => "2023-11-11"
   */
  get ISODate(): string {
    return this.dt.toISODate() ?? "";
  }

  /**********
   * Date
   *    Translates date into ISO date string
   */
  get Date(): Date {
    return this.dt.toJSDate();
  }

  /**********
   * numDaysInMonth
   *    Returns # of days in the month of the given date
   */
  get numDaysInMonth(): number {
    return this.dt.daysInMonth ?? 0;
  }

  /**********
   * valueOf
   *    Returns milliseconds since epoch
   *
   *    **NOTE**: Not intended for comparisons. See BaseDateBuilder
   */
  get valueOf(): number {
    return this.dt.valueOf();
  }

  /**********
   * secondsSinceEpoch
   *    Returns seconds since epoch
   *
   *    **NOTE**: Not intended for comparisons. See BaseDateBuilder
   */
  get secondsSinceEpoch(): number {
    return this.valueOf / 1000;
  }

  /**********
   * year
   *
   *    Get the year
   */
  get year(): number {
    return this.dt.year;
  }

  /**********
   * month
   *
   *    Get the month (1-12).
   */
  get month(): number {
    return this.dt.month;
  }

  /**********
   * day
   *
   *    Get the day of the month (1-30ish).
   */
  get day(): number {
    return this.dt.day;
  }

  /**********
   * hour
   *
   *    Get the hour of the day (0-23).
   */
  get hour(): number {
    return this.dt.hour;
  }

  /**********
   * minute
   *
   *    Get the minute of the hour (0-59).
   */
  get minute(): number {
    return this.dt.minute;
  }

  /**********
   * second
   *
   *    Get the second of the minute (0-59).
   */
  get second(): number {
    return this.dt.second;
  }

  /****
   * DateTime
   *    Returns a DateTime instance
   *
   * @deprecated all DateTime logic should be encapsulated in date builders.
   */
  get DateTime(): DateTime {
    return this.dt;
  }
}

/**********
 * DateBuilder
 *    Performs date math and timezone manipulation
 */
export class DateBuilder extends BaseDateBuilder {
  /*******
   * add
   *    Adds duration
   *    Use negative values to perform subtraction
   *      Ex. dateBuilder("2023-10-10").add({ years: -1, days: 1 }).ISODate => "2022-10-11"
   *
   * @param years
   * @returns DateBuilder
   */
  add(duration: DurationLikeObject): DateBuilder {
    return new DateBuilder(this.dt.plus(duration));
  }

  /*******
   * startOf
   *    Transforms date to start of unit
   *      Ex. dateBuilder("2023-10-10T12:00:00.000Z").startOf("day").ISO => "2023-10-10T00:00:00.000Z"
   *      Ex. dateBuilder("2023-10-10T12:00:00.000Z").startOf("year").ISO => "2023-01-01T00:00:00.000Z"
   *
   * @returns date builder
   */
  startOf(unit: DateTimeUnit): DateBuilder {
    return new DateBuilder(this.dt.startOf(unit));
  }

  /*******
   * endOf
   *    Transforms date to endOf of unit
   *      Ex. dateBuilder("2023-10-10T12:00:00.000Z").endOf("day").ISO => "2023-10-10T23:59:59.999Z"
   *      Ex. dateBuilder("2023-10-10T12:00:00.000Z").endOf("year").ISO => "2023-12-31T23:59:59.999Z"
   *
   * @returns date builder
   */
  endOf(unit: DateTimeUnit): DateBuilder {
    return new DateBuilder(this.dt.endOf(unit));
  }

  /*******
   * setZone
   *    Converts date into timezone
   *    Cannot perform math operations after this action
   *    https://moment.github.io/luxon/api-docs/index.html#datetimesetzone
   *    See [list of support timezones](https://en.wikipedia.org/wiki/List_of_tz_database_time_zones)
   *
   * @param {string} tz
   * @param opts
   * @param {boolean} [opts.keepLocalTime=false] - If true, adjust the underlying time so that the local time stays the same, but in the target zone. You should rarely need this.
   * @param {boolean} [opts.keepCalendarTime=false]
   * @returns BaseDateBuilder
   */
  setZone(tz: string, opts?: { keepLocalTime?: boolean; keepCalendarTime?: boolean }): BaseDateBuilder {
    return new BaseDateBuilder(this.dt.setZone(tz, opts));
  }

  /*******
   * utc
   *    Converts date into UTC timezone
   *    Cannot perform math operations after this action
   *
   * @returns BaseDateBuilder
   */
  utc(): BaseDateBuilder {
    return this.setZone("UTC");
  }

  /*******
   * utcInLocalTime
   *    Transforms date to UTC in local time
   *
   * @deprecated Only use this if you are working with a date created by concatenting reportDate and interval time together. Will not produce desired result in any other use case.
   * @returns BaseDateBuilder
   */
  utcInLocalTime(): BaseDateBuilder {
    return new BaseDateBuilder(this.dt.setZone("UTC", { keepLocalTime: true }));
  }
}

/*******
 * translateDateLikeToDateTime
 *    Converts a DateLike input to a DateTime instance
 *
 * @param {DateLike} dateInput
 * @param {DateBuilderOptions} [options=]
 */
const translateDateLikeToDateTime = (dateInput: DateLike, options: DateBuilderOptions = {}): DateTime => {
  if (isString(dateInput)) {
    if (options.format) {
      return DateTime.fromFormat(dateInput, options.format);
    }

    return DateTime.fromISO(dateInput);
  }

  if (isNumber(dateInput)) {
    return DateTime.fromMillis(dateInput);
  }

  if (isDate(dateInput)) {
    return DateTime.fromJSDate(dateInput);
  }

  return dateInput;
};
